import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Tv, LogOut, Home, Receipt, UserCog, Loader2, RefreshCw,
  ArrowUpRight, ShieldCheck, CheckCircle2, XCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function useCareAuth() {
  const navigate = useNavigate();
  const token = localStorage.getItem('care_token');
  const mobile = localStorage.getItem('care_mobile');
  useEffect(() => {
    if (!token) navigate('/care');
  }, [token, navigate]);
  const authFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(opts.headers || {}),
      },
    });
    if (res.status === 401) {
      localStorage.removeItem('care_token');
      localStorage.removeItem('care_mobile');
      navigate('/care');
      throw new Error('Session expired');
    }
    return res;
  }, [token, navigate]);
  return { token, mobile, authFetch };
}

function StatusBadge({ status }) {
  if (status === 'active')  return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>;
  if (status === 'expired') return <Badge className="bg-rose-100 text-rose-700 border-rose-200">Expired</Badge>;
  return <Badge className="bg-slate-100 text-slate-700 border-slate-200">{status}</Badge>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  // Backend stores end_date as "dd-mm-yyyy" already; if ISO, coerce.
  if (/^\d{2}-\d{2}-\d{4}$/.test(iso)) return iso;
  try { return new Date(iso).toLocaleDateString('en-IN'); } catch { return iso; }
}

// ---------- Renew / Change Package dialog ----------

function RenewDialog({ subscription, onClose, authFetch, onPaid }) {
  const [packages, setPackages] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`/api/care/subscriptions/${subscription.subscription_id}/packages`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || 'Failed to load packages');
        setPackages(j.packages);
        setChosen(j.packages.find(p => p.current) || j.packages[0]);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [subscription.subscription_id, authFetch]);

  const proceed = async () => {
    if (!chosen) return;
    setInitiating(true);
    try {
      const r = await authFetch(`/api/care/renew/initiate`, {
        method: 'POST',
        body: JSON.stringify({
          subscription_id: subscription.subscription_id,
          package_id: chosen.package_id,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to start payment');
      // Remember what we're paying for so the return page can poll.
      localStorage.setItem('care_last_txnid', j.txnid);
      window.location.href = j.payment_url;
    } catch (e) {
      toast.error(e.message);
      setInitiating(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Renew / Change Plan</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Subscriber: {subscription.subscriber_name || subscription.customer_name}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading plans…
          </div>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {packages.map(p => (
              <label key={p.package_id}
                className={`flex items-center justify-between border rounded-lg p-3 cursor-pointer transition
                  ${chosen?.package_id === p.package_id
                    ? 'border-indigo-500 bg-indigo-50/60 ring-2 ring-indigo-200'
                    : 'border-slate-200 hover:border-slate-300'}`}
                data-testid={`care-pkg-option-${p.package_id}`}
              >
                <div className="flex items-center gap-3">
                  <input type="radio" name="pkg" className="accent-indigo-600"
                    checked={chosen?.package_id === p.package_id}
                    onChange={() => setChosen(p)} />
                  <div>
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      {p.name}
                      {p.current && <Badge className="bg-slate-100 text-slate-600 border text-[10px] px-1 py-0">Current</Badge>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {p.validity_days} days
                      {p.channels != null && ` · ${p.channels} channels`}
                      {p.hd_channels ? ` · ${p.hd_channels} HD` : ''}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-slate-800">₹{p.price.toFixed(2)}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="care-renew-cancel">Cancel</Button>
          <Button onClick={proceed} disabled={!chosen || initiating}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="care-renew-pay-btn">
            {initiating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {chosen ? `Pay ₹${chosen.price.toFixed(2)} & ${chosen.current ? 'Renew' : 'Change Plan'}` : 'Pay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Tabs ----------

function HomeTab({ authFetch }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewFor, setRenewFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/care/me');
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to load');
      setMe(j);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!me) return null;

  const all = [...me.active_subscriptions, ...me.expired_subscriptions];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Hello, {me.profile?.name || 'Subscriber'}</h2>
          <p className="text-xs text-slate-500">You have {me.counts.active} active and {me.counts.expired} expired subscription{me.counts.total === 1 ? '' : 's'}.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="care-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {all.length === 0 && (
        <Card className="p-6 text-center text-slate-500">
          No IPTV subscriptions found for this number. Please contact your local operator.
        </Card>
      )}

      <div className="grid gap-3">
        {all.map(s => (
          <Card key={s.subscription_id} className="p-4" data-testid={`care-sub-card-${s.subscription_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-800 truncate">{s.package_name || 'IPTV Plan'}</div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Ends {fmtDate(s.end_date)} · Mobile +91 {s.mobile_no}
                </div>
                {s.subscriber_name && (
                  <div className="text-xs text-slate-400 mt-0.5 truncate">Subscriber: {s.subscriber_name}</div>
                )}
              </div>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
                onClick={() => setRenewFor(s)}
                data-testid={`care-renew-btn-${s.subscription_id}`}>
                {s.status === 'expired' ? <><ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Reactivate</> :
                                          <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Renew / Upgrade</>}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {renewFor && (
        <RenewDialog subscription={renewFor}
          authFetch={authFetch}
          onClose={() => setRenewFor(null)}
          onPaid={() => { setRenewFor(null); load(); }} />
      )}
    </div>
  );
}

function PaymentsTab({ authFetch }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch('/api/care/payments');
        const j = await r.json();
        setRows(j.payments || []);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, [authFetch]);

  if (loading) return <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-800">Payment history</h2>
      {rows.length === 0 && (
        <Card className="p-6 text-center text-slate-500">No payments yet.</Card>
      )}
      {rows.map(p => (
        <Card key={p.payment_id} className="p-3 flex items-center justify-between" data-testid={`care-payment-row-${p.txnid}`}>
          <div>
            <div className="text-sm text-slate-800 flex items-center gap-2">
              <span className="font-medium">₹{Number(p.amount).toFixed(2)}</span>
              {p.status === 'success'  && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Paid</Badge>}
              {p.status === 'pending'  && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Pending</Badge>}
              {p.status === 'failed'   && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Failed</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {p.is_upgrade ? 'Plan change' : 'Renewal'} · {fmtDate(p.confirmed_at || p.created_at)}
            </div>
            <div className="text-[10px] text-slate-400 font-mono mt-0.5">{p.txnid}</div>
          </div>
          {p.new_end_date && <div className="text-xs text-slate-500 text-right">New expiry<br /><span className="font-medium text-slate-700">{p.new_end_date}</span></div>}
        </Card>
      ))}
    </div>
  );
}

function ProfileTab({ authFetch }) {
  const [form, setForm] = useState({ name: '', email: '', address: '', alt_phone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch('/api/care/me');
        const j = await r.json();
        if (j.profile) setForm({
          name: j.profile.name || '',
          email: j.profile.email || '',
          address: j.profile.address || '',
          alt_phone: j.profile.alt_phone || '',
        });
      } finally { setLoading(false); }
    })();
  }, [authFetch]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await authFetch('/api/care/profile', { method: 'PATCH', body: JSON.stringify(form) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      toast.success('Profile saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4 max-w-lg">
      <h2 className="text-lg font-semibold text-slate-800">Your profile</h2>
      <div className="grid gap-3">
        <div>
          <Label className="text-slate-700">Name</Label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="care-profile-name" />
        </div>
        <div>
          <Label className="text-slate-700">Email</Label>
          <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="care-profile-email" />
        </div>
        <div>
          <Label className="text-slate-700">Alternate mobile</Label>
          <Input inputMode="numeric" maxLength={10} value={form.alt_phone}
            onChange={e => setForm(f => ({ ...f, alt_phone: e.target.value.replace(/\D/g, '') }))} data-testid="care-profile-altphone" />
        </div>
        <div>
          <Label className="text-slate-700">Address</Label>
          <textarea rows={3}
            className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
            value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
            data-testid="care-profile-address" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="care-profile-save">
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Save changes
      </Button>
    </div>
  );
}

// ---------- Shell ----------

export default function CarePortal() {
  const { mobile, authFetch } = useCareAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('home');

  // Handle post-payment return: /care/payment/success or /care/payment/failure
  useEffect(() => {
    if (!location.pathname.startsWith('/care/payment/')) return;
    const params = new URLSearchParams(location.search);
    const txnid = params.get('txnid') || localStorage.getItem('care_last_txnid');
    const failed = location.pathname.endsWith('/failure');
    if (failed) {
      toast.error('Payment failed. Please try again.');
      navigate('/care/home', { replace: true });
      return;
    }
    if (!txnid) { navigate('/care/home', { replace: true }); return; }
    let cancelled = false;
    const poll = async (attempts = 0) => {
      if (cancelled || attempts > 15) {
        toast.info('Payment received — subscription will be updated shortly.');
        navigate('/care/home', { replace: true });
        return;
      }
      try {
        const r = await authFetch(`/api/care/payments/status/${txnid}`);
        const j = await r.json();
        if (j.status === 'success') {
          toast.success(`Renewed! New expiry: ${j.new_end_date}`);
          localStorage.removeItem('care_last_txnid');
          navigate('/care/home', { replace: true });
          return;
        }
        if (j.status === 'failed') {
          toast.error(`Payment failed: ${j.error || 'Please retry'}`);
          navigate('/care/home', { replace: true });
          return;
        }
      } catch (_) { /* keep polling */ }
      setTimeout(() => poll(attempts + 1), 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [location.pathname, location.search, navigate, authFetch]);

  const logout = () => {
    localStorage.removeItem('care_token');
    localStorage.removeItem('care_mobile');
    navigate('/care');
  };

  const showPaymentReturn = location.pathname.startsWith('/care/payment/');

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
              <Tv className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800 leading-tight">ANT TV</div>
              <div className="text-[10px] text-slate-500">+91 {mobile}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="care-logout-btn">
            <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      {showPaymentReturn ? (
        <div className="max-w-md mx-auto p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-slate-600">Confirming your payment…</p>
          <p className="text-xs text-slate-400 mt-2">Don't close this window.</p>
        </div>
      ) : (
        <main className="max-w-3xl mx-auto px-4 py-4">
          {tab === 'home'     && <HomeTab     authFetch={authFetch} />}
          {tab === 'payments' && <PaymentsTab authFetch={authFetch} />}
          {tab === 'profile'  && <ProfileTab  authFetch={authFetch} />}
        </main>
      )}

      {!showPaymentReturn && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200">
          <div className="max-w-3xl mx-auto grid grid-cols-3">
            {[
              { k: 'home',     label: 'Home',     Icon: Home,    testid: 'care-tab-home' },
              { k: 'payments', label: 'Payments', Icon: Receipt, testid: 'care-tab-payments' },
              { k: 'profile',  label: 'Profile',  Icon: UserCog, testid: 'care-tab-profile' },
            ].map(({ k, label, Icon, testid }) => (
              <button key={k} onClick={() => setTab(k)} data-testid={testid}
                className={`py-3 flex flex-col items-center gap-0.5 text-[11px]
                  ${tab === k ? 'text-indigo-600' : 'text-slate-500'}`}>
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
          <div className="h-[env(safe-area-inset-bottom)]" />
        </nav>
      )}
    </div>
  );
}

// Success/failure return pages render this same shell — they detect the
// route via useLocation() inside CarePortal.
export { CarePortal };
export const CarePaymentReturn = CarePortal;
